import { useEffect, useMemo, useState } from 'react';
import { Plus, GitBranch, Search, CheckCircle2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { playbooks as playbooksApi } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { Loader } from '../components/UI/LoadingSpinner';
import PlaybookForm from '../components/PlaybookSettings/PlaybookForm';
import StepsEditor from '../components/PlaybookSettings/StepsEditor';
import { TextField, Checkbox } from '@anyemp/ui-kit';

const NEW_PLAYBOOK_ID = '__new__';

const PlaybookSettings = () => {
  const { hasPermission } = useAuth();

  const canEdit = useMemo(
    () => hasPermission('talent-service', 'playbook', 'update'),
    [hasPermission]
  );

  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await playbooksApi.list();
      setPlaybooks(res?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load playbooks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredPlaybooks = useMemo(() => {
    let list = playbooks;
    if (!showInactive) list = list.filter((p) => p.is_active);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    return list;
  }, [playbooks, search, showInactive]);

  // Pick the first playbook on initial load (when nothing selected yet)
  useEffect(() => {
    if (selectedId === null && filteredPlaybooks.length > 0) {
      setSelectedId(filteredPlaybooks[0].id);
    }
  }, [filteredPlaybooks, selectedId]);

  const isNew = selectedId === NEW_PLAYBOOK_ID;
  const selected = isNew ? null : playbooks.find((p) => p.id === selectedId);

  const handleNewClick = () => {
    setSelectedId(NEW_PLAYBOOK_ID);
  };

  const handleCreate = async (payload) => {
    try {
      const res = await playbooksApi.create(payload);
      const created = res?.data;
      toast.success('Playbook created');
      await load();
      if (created?.id) setSelectedId(created.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create');
      throw err;
    }
  };

  const handleUpdate = async (payload) => {
    if (!selected) return;
    try {
      await playbooksApi.update(selected.id, payload);
      toast.success('Saved');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
      throw err;
    }
  };

  const handleDeactivate = async () => {
    if (!selected) return;
    try {
      await playbooksApi.deactivate(selected.id);
      toast.success(`"${selected.name}" deactivated`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate');
    }
  };

  const handleAddStep = async (draft) => {
    if (!selected) return;
    try {
      await playbooksApi.addStep(selected.id, draft);
      toast.success('Step added');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add step');
      throw err;
    }
  };

  const handleUpdateStep = async (stepId, draft) => {
    try {
      await playbooksApi.updateStep(stepId, draft);
      toast.success('Step saved');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save step');
      throw err;
    }
  };

  const handleDeleteStep = async (stepId) => {
    try {
      await playbooksApi.deleteStep(stepId);
      toast.success('Step deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete step');
    }
  };

  const handleReorderSteps = async (fromIdx, toIdx) => {
    if (!selected || !selected.steps || toIdx < 0 || toIdx >= selected.steps.length) return;
    const orderedIds = [...selected.steps].sort((a, b) => a.display_order - b.display_order).map((s) => s.id);
    const [moved] = orderedIds.splice(fromIdx, 1);
    orderedIds.splice(toIdx, 0, moved);
    try {
      await playbooksApi.reorderSteps(selected.id, orderedIds);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reorder');
    }
  };

  const sortedSteps = useMemo(() => {
    if (!selected?.steps) return [];
    return [...selected.steps].sort((a, b) => a.display_order - b.display_order);
  }, [selected]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" translate="no">
      <title>Playbooks | Talent Service</title>

      <div className="mb-4">
        <h1 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Playbooks</h1>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
          Manage lifecycle stage templates (Onboarding, Probation, Off-boarding, …). Edits don't affect already-running instances.
        </p>
      </div>

      <div className="grid min-h-0 grid-cols-1 gap-4 lg:min-h-[calc(100vh-200px)] lg:grid-cols-[280px_minmax(0,1fr)]">
        {/* Left: list */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-lg p-3 flex flex-col">
          <div className="flex items-center gap-1.5 mb-2">
            <div className="flex-1">
              <TextField
                size="small"
                fullWidth
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startAdornment={<Search className="h-3.5 w-3.5 text-light-text-tertiary" />}
              />
            </div>
            {canEdit && (
              <button
                onClick={handleNewClick}
                className="inline-flex items-center justify-center w-8 h-8 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded"
                title="New playbook"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="mb-2">
            <Checkbox
              label="Show inactive"
              color="primary"
              size="small"
              checked={!!showInactive}
              onChange={(checked) => setShowInactive(checked)}
            />
          </div>

          <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
            {filteredPlaybooks.length === 0 ? (
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary text-center py-4">No playbooks</p>
            ) : (
              filteredPlaybooks.map((pb) => (
                <button
                  key={pb.id}
                  onClick={() => setSelectedId(pb.id)}
                  className={`w-full text-left px-2.5 py-2 rounded text-sm transition-colors ${
                    selectedId === pb.id
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100'
                      : 'hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-light-text-primary dark:text-dark-text-primary'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{pb.name}</span>
                    {pb.is_active
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      : <XCircle className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary">
                    {pb.talent_type && <span>{pb.talent_type}</span>}
                    {pb.talent_type && <span>·</span>}
                    <span>{pb.steps?.length || 0} steps</span>
                    <span>·</span>
                    <span>{pb.default_duration_days}d</span>
                  </div>
                </button>
              ))
            )}
          </div>

          {selectedId === NEW_PLAYBOOK_ID && (
            <div className="mt-2 pt-2 border-t border-light-border-primary dark:border-dark-border-primary">
              <p className="text-xs text-primary-600 dark:text-primary-400 italic px-2 py-1">+ New playbook (unsaved)</p>
            </div>
          )}
        </div>

        {/* Right: editor */}
        <div className="min-w-0 rounded-lg border border-light-border-primary bg-light-bg-secondary p-4 dark:border-dark-border-primary dark:bg-dark-bg-secondary">
          {!isNew && !selected ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-light-text-tertiary dark:text-dark-text-tertiary py-12">
              <GitBranch className="h-12 w-12 opacity-30 mb-3" />
              <p className="text-sm">Select a playbook to edit, or create a new one</p>
            </div>
          ) : (
            <div className="space-y-6">
              <PlaybookForm
                playbook={selected}
                canEdit={canEdit}
                isNew={isNew}
                onSave={isNew ? handleCreate : handleUpdate}
                onDeactivate={handleDeactivate}
              />

              {!isNew && (
                <div className="border-t border-light-border-primary dark:border-dark-border-primary pt-4">
                  <StepsEditor
                    steps={sortedSteps}
                    canEdit={canEdit}
                    onAdd={handleAddStep}
                    onUpdate={handleUpdateStep}
                    onDelete={handleDeleteStep}
                    onReorder={handleReorderSteps}
                  />
                </div>
              )}

              {isNew && (
                <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary italic">
                  Save the playbook first, then add steps.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaybookSettings;
